/**
 * Utilitários para manipulação segura de datas
 */

// Constantes para padronizar dias da semana
export const DIAS_SEMANA = {
  DOMINGO: 0,
  SEGUNDA: 1,
  TERCA: 2,
  QUARTA: 3,
  QUINTA: 4,
  SEXTA: 5,
  SABADO: 6
};

export const DIAS_SEMANA_NOMES = [
  'domingo',
  'segunda',
  'terca',
  'quarta',
  'quinta',
  'sexta',
  'sabado'
];

export const DIAS_SEMANA_ABREVIADOS = [
  'Dom.',
  'Seg.',
  'Ter.',
  'Qua.',
  'Qui.',
  'Sex.',
  'Sáb.'
];

export const MESES_NOMES = [
  'janeiro',
  'fevereiro',
  'março',
  'abril',
  'maio',
  'junho',
  'julho',
  'agosto',
  'setembro',
  'outubro',
  'novembro',
  'dezembro'
];

export const MESES_ABREVIADOS = [
  'jan',
  'fev',
  'mar',
  'abr',
  'mai',
  'jun',
  'jul',
  'ago',
  'set',
  'out',
  'nov',
  'dez'
];

/**
 * Converte data string YYYY-MM-DD para objeto Date
 * garantindo timezone segura
 */
export function stringParaData(dataString: string): Date {
  if (!dataString) return new Date();
  
  // Remover parte T se existir
  const dataPura = dataString.split('T')[0];
  
  // Criar com meio-dia para evitar problemas de timezone
  const [ano, mes, dia] = dataPura.split('-').map(Number);
  return new Date(ano, mes - 1, dia, 12, 0, 0);
}

/**
 * Formata data para exibição
 * @param data Data no formato YYYY-MM-DD ou objeto Date
 * @param formato 'dd/mm/yyyy', 'dd/mm', etc.
 */
export function formatarData(data: string | Date, formato = 'dd/mm/yyyy'): string {
  try {
    let dataObj: Date;
    
    // Converter para objeto Date se for string
    if (typeof data === 'string') {
      dataObj = stringParaData(data);
    } else if (data instanceof Date) {
      dataObj = data;
    } else {
      throw new Error('Formato de data inválido');
    }
    
    const dia = dataObj.getDate().toString().padStart(2, '0');
    const mes = (dataObj.getMonth() + 1).toString().padStart(2, '0');
    const ano = dataObj.getFullYear();
    
    switch (formato) {
      case 'dd/mm/yyyy':
        return `${dia}/${mes}/${ano}`;
      case 'dd/mm':
        return `${dia}/${mes}`;
      case 'yyyy-mm-dd':
        return `${ano}-${mes}-${dia}`;
      default:
        return `${dia}/${mes}/${ano}`;
    }
  } catch (error) {
    console.error('[DateUtils] Erro ao formatar data:', error);
    return String(data);
  }
}

/**
 * Obtém o dia da semana (0-6) de uma data
 */
export function obterDiaSemana(data: string | Date): number {
  try {
    if (typeof data === 'string') {
      return stringParaData(data).getDay();
    }
    return data.getDay();
  } catch (error) {
    console.error('[DateUtils] Erro ao obter dia da semana:', error);
    return -1;
  }
}

/**
 * Obtém o nome do dia da semana
 */
export function obterNomeDiaSemana(data: string | Date): string {
  const diaSemana = obterDiaSemana(data);
  return diaSemana >= 0 ? DIAS_SEMANA_NOMES[diaSemana] : '';
}

/**
 * Obtém a abreviação do dia da semana
 */
export function obterDiaSemanaAbreviado(data: string | Date): string {
  const diaSemana = obterDiaSemana(data);
  return diaSemana >= 0 ? DIAS_SEMANA_ABREVIADOS[diaSemana] : '';
}

/**
 * Cria título para exibição do dia: "Seg, 28/07"
 */
export function criarTituloDia(data: string | Date): string {
  try {
    const dataObj = typeof data === 'string' ? stringParaData(data) : data;
    const abrev = DIAS_SEMANA_ABREVIADOS[dataObj.getDay()];
    const dia = dataObj.getDate().toString().padStart(2, '0');
    const mes = (dataObj.getMonth() + 1).toString().padStart(2, '0');
    
    return `${abrev}, ${dia}/${mes}`;
  } catch (error) {
    console.error('[DateUtils] Erro ao criar título do dia:', error);
    return String(data);
  }
}
